import { useState, useEffect, lazy, Suspense } from 'react';
import { Share2, Lock, ChevronRight, Star } from 'lucide-react';
import { useLocation, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import ReactLoading from 'react-loading';
import Header from '../../components/dashboard-layout/Header';
import Footer from '../../components/dashboard-layout/Footer';
import MarkdownRenderer from '../../components/markdown/MarkdownRenderer';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import ShareCourseDialog from '../../components/forms/ShareCourseDialog';
import { useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
const StudentReviews = lazy(() => import('../../components/sections/StudentReviews'));

export default function CoverPageCourseLanding() {
    const [course, setCourse] = useState(null);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [courseTopics, setCourseTopics] = useState([]);
    const [expandedId, setExpandedId] = useState(null);

    const search = useSearch({ from: '/cover-course/$courseSlug' });
    const [activeTab, setActiveTab] = useState(search.tab || 'overview');

    const navigate = useNavigate();
    const { courseSlug } = useParams({ from: '/cover-course/$courseSlug' });
    const { isSignedIn } = useUser();
    const location = useLocation();

    useEffect(() => {
        // Keep activeTab in sync with URL changes (e.g., back/forward browser buttons)
        setActiveTab(search.tab || 'overview');
    }, [search.tab]);

    const isEnrolled = false; // Public page, user is never enrolled

    const courseQuery = useQuery({
        queryKey: queryKeys.courseBySlug(courseSlug),
        queryFn: () => api.getCourseBySlug(courseSlug),
        enabled: !!courseSlug,
        onSuccess: data => setCourse(data.course),
        onError: () => navigate({ to: '/available-courses' }),
    });

    const curriculumQuery = useQuery({
        queryKey: queryKeys.courseContent(courseSlug),
        queryFn: () => api.getCourseContent(courseSlug, ''),
        enabled: !!courseSlug,
        onSuccess: data => setCourseTopics(data.topics || []),
    });

    useEffect(() => {
        const originalTitle = `${document.title} | Zindua LMS`;
        if (course?.title) {
            document.title = course.title;
        }

        return () => {
            document.title = `${originalTitle} | Zindua LMS`;
        };
    }, [course?.title]);

    if (courseQuery.isLoading || !course) {
        return (
            <div className="flex justify-center items-center h-screen bg-neutral-50">
                <ReactLoading type="spin" color="#fe3448" height={50} width={50} />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
            <Header />
            <main className="max-w-350 mx-auto grow mt-20 px-4 sm:px-6 lg:px-8 w-full">
                <div className="">
                    {/* Header */}
                    <div className="bg-neutral-50">
                        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                            <h1 className="text-h1 sm:text-4xl font-bold text-neutral-950 mb-2 capitalize">{course.title}</h1>
                            <p className="text-neutral-700 text-sm sm:text-base">{course.summary}</p>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="px-4 lg:px-8 py-2">
                        <div className="flex flex-col-reverse lg:flex-row gap-8 w-full">
                            <Tabs
                                className="w-full lg:w-2/3 flex flex-col gap-4"
                                value={activeTab}
                                onValueChange={val => {
                                    setActiveTab(val);
                                    const newSearch = { ...search, tab: val };
                                    navigate({
                                        to: location.pathname,
                                        search: newSearch,
                                        replace: true,
                                    });
                                }}
                            >
                                <div className="flex flex-row gap-4 justify-between md:max-w-3xl max-md:justify-center max-md:items-center max-md:w-full">
                                    <TabsList className="justify-center max-md:w-full">
                                        <TabsTrigger
                                            value="overview"
                                            className="capitalize justify-center max-md:w-full max-md:text-xs"
                                        >
                                            overview
                                        </TabsTrigger>
                                        <TabsTrigger
                                            value="curriculum"
                                            className="capitalize justify-center max-md:w-full max-md:text-xs"
                                        >
                                            Curriculum
                                        </TabsTrigger>
                                        <TabsTrigger
                                            value="reviews"
                                            className="capitalize justify-center max-md:w-full max-md:text-xs"
                                        >
                                            Reviews
                                        </TabsTrigger>
                                    </TabsList>
                                </div>
                                {/* Left Column - Course Details */}
                                <div className="">
                                    {/* Course Overview */}
                                    <TabsContent value="overview">
                                        <div className="bg-neutral-50 rounded-3xl py-4">
                                            <h2 className="mb-2 capitalize">Course Overview</h2>
                                            <div className="text-neutral-700 leading-relaxed">
                                                {course.description ? (
                                                    <MarkdownRenderer content={course.description} />
                                                ) : (
                                                    <p>No overview available.</p>
                                                )}
                                            </div>
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="curriculum">
                                        <div className="bg-neutral-50 rounded-3xl py-4">
                                            <h2 className="mb-4">Course Curriculum</h2>
                                            {curriculumQuery.isLoading ? (
                                                <div className="flex justify-center py-8">
                                                    <ReactLoading type="spin" color="#fe3448" height={50} width={50} />
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-4">
                                                    {courseTopics?.map((item, index) => (
                                                        <div
                                                            key={index}
                                                            className="border border-neutral-300 rounded-2xl overflow-hidden"
                                                        >
                                                            <div
                                                                className="flex justify-between items-center p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                                                                onClick={() => setExpandedId(expandedId === index ? null : index)}
                                                            >
                                                                <h4 className="">
                                                                    {item.topic.order}. {item.topic.name}
                                                                </h4>
                                                                <ChevronRight
                                                                    className={`transition-transform duration-300 text-neutral-950 font-bold ${
                                                                        expandedId === index ? 'rotate-90' : ''
                                                                    }`}
                                                                    size={20}
                                                                />
                                                            </div>
                                                            {expandedId === index && (
                                                                <div className="bg-neutral-50 text-neutral-700">
                                                                    <ul className="list-none">
                                                                        {item.lessons?.map((lesson, i) => (
                                                                            <li key={`lesson-${i}`}>
                                                                                <div className="w-full flex items-center p-4 pl-4 md:pl-8 text-left opacity-50 cursor-not-allowed">
                                                                                    <Lock
                                                                                        size={24}
                                                                                        className="mr-3 text-neutral-700 shrink-0"
                                                                                    />
                                                                                    <p className="text-neutral-700">
                                                                                        {lesson.title}
                                                                                    </p>
                                                                                </div>
                                                                            </li>
                                                                        ))}
                                                                        {item.quizes?.map((quiz, i) => (
                                                                            <li key={`quiz-${i}`}>
                                                                                <div className="w-full flex items-center p-4 pl-4 md:pl-8 text-left opacity-50 cursor-not-allowed">
                                                                                    <Lock
                                                                                        size={24}
                                                                                        className="mr-3 text-neutral-700 shrink-0"
                                                                                    />
                                                                                    <p className="text-neutral-700">
                                                                                        Quiz: {quiz.title}
                                                                                    </p>
                                                                                </div>
                                                                            </li>
                                                                        ))}

                                                                        {item.projects?.map((project, i) => (
                                                                            <li key={`project-${i}`}>
                                                                                <div className="w-full flex items-center p-4 pl-4 md:pl-8 text-left opacity-50 cursor-not-allowed">
                                                                                    <Lock
                                                                                        size={24}
                                                                                        className="mr-3 text-neutral-700 shrink-0"
                                                                                    />
                                                                                    <p className="text-neutral-700">
                                                                                        Project: {project.title}
                                                                                    </p>
                                                                                </div>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="reviews">
                                        <h2 className="py-4">Student Reviews</h2>
                                        <Suspense
                                            fallback={
                                                <div className="flex justify-center py-8">
                                                    <ReactLoading type="spin" color="#fe3448" height={50} width={50} />
                                                </div>
                                            }
                                        >
                                            <StudentReviews courseId={course.id} isEnrolled={isEnrolled} />
                                        </Suspense>
                                    </TabsContent>
                                </div>
                            </Tabs>

                            {/* Right Column - Video & Enrollment */}
                            <div className="w-full lg:w-1/3">
                                <div className="bg-neutral-50 border border-neutral-300 p-2 rounded-2xl overflow-hidden sticky top-20">
                                    {/* Video Thumbnail */}
                                    <div className="relative bg-neutral-100 rounded-lg aspect-video flex items-center justify-center">
                                        <img
                                            src={
                                                course.thumbnail ||
                                                'https://lms-internal.zinduaschool.com/assets/thumbnails/default-course/default-thumbnail.png'
                                            }
                                            alt="Course preview"
                                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-32.5 max-h-32.5 object-contain"
                                        />
                                    </div>

                                    {/* CTA */}
                                    <div className="p-6 flex flex-col gap-4">
                                        <>
                                            <div className="flex flex-row items-center justify-between">
                                                <div className="text-center py-2">
                                                    <p className="text-xl font-bold text-neutral-900">
                                                        {course?.price === 0
                                                            ? 'FREE'
                                                            : course?.price && typeof course.price === 'number'
                                                              ? `KES ${course.price.toLocaleString()}`
                                                              : ''}
                                                    </p>
                                                </div>
                                                {course.total_rating !== null && course.total_rating !== undefined && (
                                                    <div className="flex items-center justify-center pb-2">
                                                        <div className="flex items-center">
                                                            <Star className="w-5 h-5 text-yellow-400" />
                                                        </div>
                                                        <span className="text-sm text-neutral-700 ml-1">
                                                            {course.total_rating}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant={'destructive'}
                                                        onClick={() => {
                                                            if (!isSignedIn) {
                                                                navigate({
                                                                    to: '/sign-in',
                                                                    search: {
                                                                        from: location.pathname,
                                                                    },
                                                                });
                                                                return;
                                                            }
                                                            // For signed-in users, if the course is free, navigate to enroll.
                                                            // If paid, the Popover will open.
                                                            if (course.price === 0) {
                                                                navigate({
                                                                    to: '/course/$courseSlug',
                                                                    params: {
                                                                        courseSlug: course.slug,
                                                                    },
                                                                });
                                                            }
                                                        }}
                                                        aria-disabled={
                                                            courseQuery.isLoading || (isSignedIn && course.price !== 0)
                                                        }
                                                        className={`w-full flex items-center justify-center gap-2 ${
                                                            courseQuery.isLoading || (isSignedIn && course.price !== 0)
                                                                ? 'bg-neutral-400 cursor-not-allowed'
                                                                : ''
                                                        }`}
                                                    >
                                                        <span>{isSignedIn ? 'Enroll Now' : 'Login to Enroll'}</span>
                                                        <ChevronRight size={18} />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="px-4 py-2">
                                                    <p>Contact the Course manager to be enrolled.</p>
                                                </PopoverContent>
                                            </Popover>
                                        </>

                                        <Button
                                            variant={'link'}
                                            className="w-full font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                                            onClick={() => setShareDialogOpen(true)}
                                        >
                                            <span>Share Course</span>
                                            <Share2 size={18} />
                                        </Button>
                                    </div>
                                    <ShareCourseDialog open={shareDialogOpen} onOpenChange={setShareDialogOpen} course={course} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
